import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, Clock, XCircle, Gift } from "lucide-react";
import { clearRefCode } from "@/lib/referral";
import { useCart } from "@/context/CartContext";

/**
 * Fire a GA4 `purchase` event exactly once per Stripe session_id.
 * De-duplicated via sessionStorage so refreshing/back-navigating won't double-count.
 */
function trackPurchase(data) {
  if (!data || !data.transaction_id) return;
  try {
    const key = `folio_ga_purchase_${data.transaction_id}`;
    if (sessionStorage.getItem(key)) return;
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "purchase", {
        transaction_id: data.transaction_id,
        value: Number(data.amount) || 0,
        currency: (data.currency || "usd").toUpperCase(),
        items: Array.isArray(data.items) ? data.items : [],
      });
    }
    sessionStorage.setItem(key, "1");
  } catch (err) {
    // Analytics failures must never break the success page.
    console.warn("[ga] purchase tracking skipped:", err);
  }
}

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | failed | expired
  const attemptsRef = useRef(0);
  const navigate = useNavigate();
  const { clear: clearCart } = useCart();

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          clearRefCode();
          clearCart();
          trackPurchase(data);
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= 8) {
          setStatus("failed");
          return;
        }
        setTimeout(poll, 2000);
      } catch (err) {
        console.warn("[checkout] status poll failed:", err);
        setStatus("failed");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, clearCart]);

  return (
    <div data-testid="checkout-success-page" className="max-w-2xl mx-auto px-6 py-32 text-center">
      {status === "polling" && (
        <>
          <Clock className="w-16 h-16 mx-auto text-sage" strokeWidth={1} />
          <h1 className="font-serif text-5xl mt-8">Confirming your purchase…</h1>
          <p className="text-muted_ink mt-4">Sit tight while we wrap your ebook.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="w-16 h-16 mx-auto text-terracotta" strokeWidth={1.25} />
          <h1 className="font-serif text-5xl mt-8">Thank you.</h1>
          <p className="text-muted_ink mt-4 text-lg">Your book is now on your shelf, ready to read.</p>
          <div className="mt-10 flex justify-center gap-3 flex-wrap">
            <Link to="/library" data-testid="success-library-btn" className="btn-primary">Open my library</Link>
            <Link to="/browse" className="btn-secondary">Keep browsing</Link>
          </div>

          <div data-testid="success-referral-cta" className="mt-16 border border-edge bg-linen p-8 max-w-xl mx-auto text-left">
            <div className="flex items-start gap-4">
              <Gift className="w-6 h-6 text-terracotta mt-1 flex-shrink-0" strokeWidth={1.5} />
              <div>
                <div className="label-eyebrow text-sage">Share Folio · earn credit</div>
                <div className="font-serif text-2xl mt-2">Give friends 15% off — earn 15% back.</div>
                <p className="text-muted_ink mt-2 text-sm">
                  Send your referral link to a friend. When they buy, you get 15% of their purchase as credit you can spend on your next book.
                </p>
                <Link to="/referrals" data-testid="success-go-to-referrals" className="btn-primary mt-4 inline-flex">
                  Get my referral link
                </Link>
              </div>
            </div>
          </div>
        </>
      )}
      {(status === "failed" || status === "expired") && (
        <>
          <XCircle className="w-16 h-16 mx-auto text-muted_ink" strokeWidth={1} />
          <h1 className="font-serif text-5xl mt-8">Something went wrong.</h1>
          <p className="text-muted_ink mt-4">
            {status === "expired" ? "Your checkout session expired." : "We couldn't confirm your payment."}
          </p>
          <div className="mt-10">
            <button onClick={() => navigate("/browse")} className="btn-primary">Back to browsing</button>
          </div>
        </>
      )}
    </div>
  );
}
